import { Outlet, Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Users,
  Calendar,
  DollarSign,
  Package,
  Settings,
  LogOut,
  Building2
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useEffect } from "react";
import { useUserRole } from "@/hooks/useUserRole";

export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loading: roleLoading, isTatuador, effectiveStudioId, permissaoEstoque } = useUserRole();
  const [studioInfo, setStudioInfo] = useState<{nome: string, logo_url: string | null}>({
    nome: "Studio Tattoo",
    logo_url: null
  });

  useEffect(() => {
    if (roleLoading || !effectiveStudioId) return;

    const fetchStudioInfo = async () => {
      try {
        const { data: configData } = await supabase
          .from("configuracoes")
          .select("nome_studio, logo_url")
          .eq("studio_id", effectiveStudioId)
          .single();

        if (configData) {
          setStudioInfo({
            nome: configData.nome_studio || "Studio Tattoo",
            logo_url: configData.logo_url || null
          });
        }
      } catch (error) {
        console.error("Erro ao carregar informações do studio:", error);
      }
    };

    fetchStudioInfo();

    window.addEventListener('studio-info-updated', fetchStudioInfo);
    return () => window.removeEventListener('studio-info-updated', fetchStudioInfo);
  }, [roleLoading, effectiveStudioId]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success("Sessão encerrada");
      navigate({ to: "/login", replace: true });
    } catch (error) {
      toast.error("Erro ao sair");
    }
  };

  const menuItems = [
    { name: "Dashboard", icon: LayoutDashboard, path: "/" },
    { name: "Agenda", icon: Calendar, path: "/agenda" },
    { name: "Clientes", icon: Users, path: "/clientes" },
    { name: "Financeiro", icon: DollarSign, path: "/financeiro" },
    ...(!isTatuador || permissaoEstoque === "todos" ? [{ name: "Estoque", icon: Package, path: "/estoque" }] : []),
    { name: "Configurações", icon: Settings, path: "/configuracoes" },
  ];

  const activeItem = menuItems.find(item => item.path === location.pathname) || menuItems[0];

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-background font-sans text-foreground">

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 bg-card border-r border-border flex-col sticky top-0 h-screen shadow-sm">
        <div className="px-6 pt-7 pb-4 space-y-4">
          {/* Nome do sistema */}
          <h1 className="text-lg font-bold text-foreground tracking-tight">Studio Tattoo</h1>

          {/* Bloco do estúdio — dados do Supabase */}
          <div className="flex items-center gap-3 bg-muted/60 rounded-xl px-3 py-2.5">
            {studioInfo.logo_url ? (
              <img src={studioInfo.logo_url} alt="Logo" className="size-9 rounded-lg object-cover shrink-0" />
            ) : (
              <div className="size-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Building2 size={18} className="text-primary" />
              </div>
            )}
            <span className="text-sm font-medium text-foreground line-clamp-2 leading-tight">
              {studioInfo.nome}
            </span>
          </div>
        </div>
        
        <nav className="flex-1 px-4 space-y-2 mt-4">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${
                  isActive 
                    ? "bg-red-50 text-primary shadow-sm" 
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <item.icon size={20} className={isActive ? "text-primary" : "group-hover:text-primary transition-colors"} />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
          
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group text-muted-foreground hover:bg-red-50 hover:text-primary mt-auto mb-4"
          >
            <LogOut size={20} className="group-hover:text-primary transition-colors" />
            <span className="font-medium">Sair</span>
          </button>
        </nav>
      </aside>

      {/* Main Container */}
      <div className="flex-1 flex flex-col min-h-screen relative pb-20 md:pb-0">
        {/* Top Bar — somente mobile: nome/logo do estúdio */}
        <header className="md:hidden h-16 border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-10 px-4 flex items-center gap-2 shadow-sm">
          {studioInfo.logo_url && (
            <img src={studioInfo.logo_url} alt="Logo" className="size-8 rounded-lg object-cover" />
          )}
          <h1 className="text-lg font-semibold text-foreground tracking-tight">
            {studioInfo.nome}
          </h1>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 md:p-10 animate-in fade-in duration-500">
          <Outlet />
        </main>

        {/* Mobile Bottom Navigation */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-card/95 backdrop-blur-lg border-t border-border flex items-center justify-around px-2 z-20 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`flex flex-col items-center justify-center gap-1 transition-all duration-200 ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <item.icon size={20} />
                <span className="text-[10px] font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>

      </div>
    </div>
  );
}

